import { Palette } from "./palette";
import type { EnemyDef } from "./enemies";
import type { Rng } from "../core/rng";

// Boss definitions. Most bosses teach one lesson; ZERO DAY ignores all of them.
//
// Design (formalized from the brief): Zero Day is the embodiment of a zero-day
// exploit — an unknown corruption with no fixed signature. Instead of a scripted
// move list it draws a RANDOM SUBSET from a large ability pool each encounter,
// so no two fights are alike and no strategy can be memorized. In Endless / True
// Glitched it returns every 100 floors with a LARGER subset — the corruption
// "evolves" alongside the player.

export interface BossDef {
  id: string;
  name: string;
  color: string;
  hp: number;
  speed: number;
  radius: number;
  /** One-line lesson this boss teaches the player. */
  teaches: string;
}

export const BOSSES: Record<string, BossDef> = {
  protocol_guardian: {
    id: "protocol_guardian",
    name: "Protocol Guardian",
    color: Palette.boss,
    hp: 1400,
    speed: 60,
    radius: 46,
    teaches: "Positioning under pressure.",
  },
  firewall: {
    id: "firewall",
    name: "Firewall",
    color: "#E68A4E",
    hp: 2000,
    speed: 40,
    radius: 52,
    teaches: "Movement through hazards.",
  },
  cascade_failure: {
    id: "cascade_failure",
    name: "Cascade Failure",
    color: "#E0A24E",
    hp: 2600,
    speed: 50,
    radius: 54,
    teaches: "Resource management.",
  },
  the_null: {
    id: "the_null",
    name: "The Null",
    color: "#5A2A8A",
    hp: 3200,
    speed: 70,
    radius: 50,
    teaches: "Fighting with the map turned off.",
  },
  firewall_member: {
    id: "firewall_member",
    name: "Corrupted Firewall",
    color: "#74b2ff",
    hp: 3600,
    speed: 78,
    radius: 50,
    teaches: "An ally lost to corruption — free them.",
  },
  zero_day: {
    id: "zero_day",
    name: "ZERO DAY",
    color: Palette.zeroDay,
    hp: 4200,
    speed: 80,
    radius: 58,
    teaches: "There are no rules. Adapt.",
  },
};

// ── ZERO DAY ability pool ───────────────────────────────────────────────────
export type ZeroDayAbilityId =
  | "packet_storm"
  | "teleport"
  | "clone"
  | "worm_swarm"
  | "firewall_walls"
  | "corruption_zones"
  | "blackout"
  | "homing_lasers"
  | "shield_generator"
  | "data_mines"
  | "chain_corruption"
  | "pulse_reflection"
  | "gravity_well"
  | "null_pulse";

export interface ZeroDayAbility {
  id: ZeroDayAbilityId;
  name: string;
  /** Seconds between casts of this ability while active. */
  cadence: number;
  description: string;
}

export const ZERO_DAY_POOL: ZeroDayAbility[] = [
  { id: "packet_storm", name: "Packet Storm", cadence: 6, description: "Bullet-hell ring of packets." },
  { id: "teleport", name: "Teleport", cadence: 4, description: "Blinks across the arena." },
  { id: "clone", name: "Clone", cadence: 12, description: "Spawns a decoy copy." },
  { id: "worm_swarm", name: "Worm Swarm", cadence: 8, description: "Summons a burst of Worms." },
  { id: "firewall_walls", name: "Firewall Walls", cadence: 10, description: "Raises moving barriers." },
  { id: "corruption_zones", name: "Corruption Zones", cadence: 7, description: "Spreads damaging floor zones." },
  { id: "blackout", name: "Blackout", cadence: 14, description: "Disables part of the arena." },
  { id: "homing_lasers", name: "Homing Lasers", cadence: 5, description: "Tracking beams lock on." },
  { id: "shield_generator", name: "Shield Generator", cadence: 16, description: "Becomes briefly invulnerable." },
  { id: "data_mines", name: "Data Mines", cadence: 9, description: "Seeds proximity mines." },
  { id: "chain_corruption", name: "Chain Corruption", cadence: 8, description: "Arcs corruption between points." },
  { id: "pulse_reflection", name: "Pulse Reflection", cadence: 11, description: "Reflects player projectiles." },
  { id: "gravity_well", name: "Gravity Well", cadence: 7, description: "Pulls PCKT toward a point." },
  { id: "null_pulse", name: "Null Pulse", cadence: 13, description: "Wave that nullifies a weapon briefly." },
];

/**
 * How many abilities Zero Day draws for a given encounter index (0-based).
 * First fight: 4 abilities, then +1 each encounter so the corruption visibly
 * "evolves", capped by the pool. idx0->4, idx1->5, idx2->6 …
 */
export function zeroDayAbilityCount(encounterIndex: number): number {
  return Math.min(4 + encounterIndex, ZERO_DAY_POOL.length);
}

/** Endless: Zero Day returns every N floors. */
export const ZERO_DAY_FLOOR_INTERVAL = 100;

// ── Mini-bosses ─────────────────────────────────────────────────────────────
// Elite, evolved forms of the regular threats. Far weaker than a stage boss —
// they punctuate the survival zones (~every 5 zones) without ending a stage.

export interface MiniBossDef extends BossDef {
  /** Attack-pattern ability ids this mini-boss cycles. */
  abilities: string[];
}

export const MINI_BOSSES: MiniBossDef[] = [
  {
    id: "viral_swarm",
    name: "Viral Swarm",
    color: Palette.virus,
    hp: 520,
    speed: 70,
    radius: 36,
    teaches: "Positioning under pressure.",
    abilities: ["volley"],
  },
  {
    id: "tunneler",
    name: "Tunneler",
    color: Palette.worm,
    hp: 460,
    speed: 96,
    radius: 32,
    teaches: "Reaction speed.",
    abilities: ["summon"],
  },
  {
    id: "fork_bomb",
    name: "Fork Bomb",
    color: Palette.glitch,
    hp: 600,
    speed: 64,
    radius: 38,
    teaches: "Crowd control on splitters.",
    abilities: ["burst"],
  },
  {
    id: "amplifier",
    name: "Amplifier",
    color: Palette.corruptor,
    hp: 680,
    speed: 58,
    radius: 40,
    teaches: "Target priority.",
    abilities: ["summon", "volley"],
  },
];

const MINI_BY_ID: Record<string, MiniBossDef> = Object.fromEntries(MINI_BOSSES.map((m) => [m.id, m]));

export function miniBossById(id: string): MiniBossDef | undefined {
  return MINI_BY_ID[id];
}

// ── Stage / zone structure ───────────────────────────────────────────────────
// A run is grouped into STAGES of ZONES_PER_STAGE zones. Most zones are pure
// survival/tower-defense. A MINI-boss appears every MINI_BOSS_INTERVAL zones; a
// STAGE (story "Day") boss appears only on the final zone of each stage.
// 6 zones per stage with a mini-boss at the 3rd zone of each stage, so a stage
// is: survive, survive, mini-boss, survive, survive, STAGE BOSS. The 6-stage
// story reaches Zero Day at zone 36 — a session-length campaign, not hours.
// (Keep ZONES_PER_STAGE = 2 × MINI_BOSS_INTERVAL so the mini roster cycles.)
export const ZONES_PER_STAGE = 6;
export const MINI_BOSS_INTERVAL = 3;

/** Fraction of a boss's max HP a Communication Purge chips off (never a kill). */
export const PURGE_BOSS_DAMAGE_FRACTION = 0.12;

// ── Story spine: which boss caps each STAGE ──────────────────────────────────
// Minion prototypes → corrupted Firewall member (penultimate) → Zero Day (final).
export const STORY_BOSSES = [
  "protocol_guardian",
  "firewall",
  "cascade_failure",
  "the_null",
  "firewall_member",
  "zero_day",
];
export const STORY_LENGTH = STORY_BOSSES.length;

// Endless / Glitched: the "Day" bosses PCKT expects at a stage finale. In
// Glitched mode the Firewall intercepts and destroys these before turning on
// PCKT, so the list excludes Firewall itself. Zero Day still returns directly.
const GLITCHED_DAY_BOSSES = ["protocol_guardian", "cascade_failure", "the_null"];
/** In endless, a real Zero Day fight every N stages (others are Glitched-Firewall). */
const ENDLESS_ZERO_DAY_EVERY = 3;

export type BossKind = "survival" | "mini" | "stage";

export interface BossPlan {
  kind: BossKind;
  /** The boss actually fought (mini id, stage id, or "firewall" when glitched). */
  bossId?: string;
  /** Glitched mode: Firewall destroys the expected Day boss, then attacks PCKT. */
  glitched?: boolean;
  /** The Day boss the Firewall destroys on-screen (glitched only). */
  expectedBossId?: string;
}

/** 0-based stage index for a zone (zones 1..10 = stage 0, 11..20 = stage 1, …). */
export function stageIndexForZone(zone: number): number {
  return Math.floor((zone - 1) / ZONES_PER_STAGE);
}

/** Is this zone the final zone of its stage (i.e. a stage-boss zone)? */
export function isStageFinale(zone: number): boolean {
  return zone % ZONES_PER_STAGE === 0;
}

/** Mini-boss id for a mini-boss zone (cycles through the FULL roster). */
export function miniBossIdForZone(zone: number): string {
  // Mini-boss zones are the multiples of 5 that aren't stage finales: 5,15,25,…
  // so zone/5 is always odd (1,3,5,7). Map by ordinal so all four cycle:
  // ord 0,1,2,3 → viral_swarm, tunneler, fork_bomb, amplifier.
  const ord = Math.floor((zone / MINI_BOSS_INTERVAL - 1) / 2);
  return MINI_BOSSES[((ord % MINI_BOSSES.length) + MINI_BOSSES.length) % MINI_BOSSES.length].id;
}

/**
 * What (if anything) ends a given zone. Stage finales spawn the story Day boss;
 * once the story is complete, endless stage finales become Glitched-Firewall
 * ambushes (with periodic real Zero Day returns). Other multiples of five are
 * mini-bosses; everything else is a survival zone.
 */
export function bossPlanForZone(zone: number, storyComplete: boolean): BossPlan {
  if (isStageFinale(zone)) {
    const sIndex = stageIndexForZone(zone);
    if (sIndex < STORY_LENGTH) {
      return { kind: "stage", bossId: STORY_BOSSES[sIndex] };
    }
    // Endless stages (after the story spine is exhausted).
    const endlessStage = sIndex - STORY_LENGTH;
    if ((endlessStage + 1) % ENDLESS_ZERO_DAY_EVERY === 0) {
      return { kind: "stage", bossId: "zero_day" };
    }
    const expected = GLITCHED_DAY_BOSSES[endlessStage % GLITCHED_DAY_BOSSES.length];
    if (storyComplete) {
      return { kind: "stage", bossId: "firewall", glitched: true, expectedBossId: expected };
    }
    return { kind: "stage", bossId: expected };
  }
  if (zone % MINI_BOSS_INTERVAL === 0) {
    return { kind: "mini", bossId: miniBossIdForZone(zone) };
  }
  return { kind: "survival" };
}

/** Stage-boss HP multiplier (scales with zone). */
export function stageBossHpScale(zone: number): number {
  return 0.4 * (1 + (zone - 1) * 0.2);
}

/** Mini-boss HP multiplier — gentler curve so they stay quick to clear. */
export function miniBossHpScale(zone: number): number {
  return 0.6 * (1 + (zone - 1) * 0.12);
}

/** 0-based Zero Day encounter number (drives how many abilities it draws). */
export function zeroDayEncounterIndex(zone: number): number {
  const sIndex = stageIndexForZone(zone);
  if (sIndex < STORY_LENGTH) return 0; // first (story) Zero Day
  const endlessStage = sIndex - STORY_LENGTH;
  return 1 + Math.floor(endlessStage / ENDLESS_ZERO_DAY_EVERY);
}

/** A boss rendered/handled as a big special Enemy (behavior "boss"). */
export function bossEnemyDef(b: BossDef): EnemyDef {
  return {
    id: b.id,
    name: b.name,
    behavior: "boss",
    color: b.color,
    hp: b.hp,
    speed: b.speed,
    damage: 16,
    radius: b.radius,
    xp: 120,
    fragments: 60,
    weight: 0,
    minWave: 0,
  };
}

/** The attack abilities a boss uses; Zero Day draws a random evolving subset. */
export function abilitiesForBoss(bossId: string, rng: Rng, encounterIndex: number): string[] {
  switch (bossId) {
    case "protocol_guardian":
      return ["burst"];
    case "firewall":
      return ["volley"];
    case "cascade_failure":
      return ["summon"];
    case "the_null":
      return ["burst", "volley"];
    case "firewall_member":
      return ["volley", "summon"];
    case "zero_day":
      return rng.sample(
        ZERO_DAY_POOL.map((a) => a.id),
        zeroDayAbilityCount(encounterIndex),
      );
    default:
      return ["burst"];
  }
}

/** Map an ability id (incl. Zero Day's pool) to an implemented attack pattern. */
export function abilityToPattern(id: string): "burst" | "volley" | "summon" | "blink" {
  switch (id) {
    case "volley":
    case "homing_lasers":
    case "chain_corruption":
    case "data_mines":
    case "firewall_walls":
      return "volley";
    case "summon":
    case "worm_swarm":
    case "clone":
    case "shield_generator":
      return "summon";
    case "blink":
    case "teleport":
      return "blink";
    default:
      return "burst";
  }
}
